import UserApi from '../api/users';
import router from '../../router';

export default {
  namespaced: true,
  state: {
    user: null,
    loadingUserProgress: true,
    userProgress: {
      courses: [],
      bigSix: null,
      videoScores: null,
      latestVideo: null,
      videosAnalyzed: 0,
      videosShared: 0,
      sessionsAttended: 0,
      coachEvaluations: 0,
      exercisesCompleted: [],
      certificate: null,
    },
    users: [],
    coaches: [],
    isAuth: false,
    tempUser: null,
    demoToken: null,
    interval: null,
    authFailureCount: 0,
  },
  getters: {
    currentUser(state) {
      return state.user;
    },
    currentUserProgress(state) {
      return state.userProgress;
    },
    isAuth(state) {
      return state.isAuth;
    },
    tempUser(state) {
      return state.tempUser;
    },
    demoToken(state) {
      return state.demoToken;
    },
    loadingUserProgress(state) {
      return state.loadingUserProgress;
    },
  },
  actions: {
    login(context, credentials) {
      return new Promise((resolve, reject) => {
        UserApi.login(credentials)
          .then((response) => {
            context.commit('SET_USER', response.user);
            context.commit('SET_AUTHENTICATED', true);

            if (import.meta.env.DEV) {
              localStorage.setItem('rheto', response.token);
            }

            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_USER', null);
            context.commit('SET_AUTHENTICATED', false);
            reject(error.response?.data?.message);
          });
      });
    },
    register(context, credentials) {
      return new Promise((resolve, reject) => {
        UserApi.register(credentials)
          .then((response) => {
            context.commit('SET_USER', response.user);
            context.commit('SET_AUTHENTICATED', true);

            if (import.meta.env.DEV) {
              localStorage.setItem('rheto', response.token);
            }
            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_USER', null);
            context.commit('SET_AUTHENTICATED', false);
            reject(error.response);
          });
      });
    },
    async startAuthPolling(context) {
      if (context.state.interval) {
        return;
      }

      const polling = setInterval(() => {
        // Poll for auth token and notifications
        context.dispatch('checkJwt').then(() => {
          // Token is valid — reset the failure streak and refresh notifications
          context.commit('RESET_AUTH_FAILURE');
          context.dispatch('notifications/getNotifications', {}, { root: true });
        }).catch((error) => {
          // Only a definitive auth failure should end the session. Transient
          // problems (offline, sleep/resume, request timeout, 5xx, backend
          // restarting) must NOT log the user out — skip this cycle and let
          // the next poll recover.
          const sessionInvalid = error?.response?.status === 401 || error?.authInvalid === true;
          if (!sessionInvalid) {
            return;
          }
          // Require two consecutive definitive failures so a single fluke 401
          // cannot drop an otherwise-valid session.
          context.commit('INCREMENT_AUTH_FAILURE');
          if (context.state.authFailureCount >= 2) {
            context.commit('CLEAR_POLLING_INTERVAL');
            context.commit('RESET_AUTH_FAILURE');
            context.dispatch('logout');
          }
        });
      }, 10000);
      context.commit('SET_POLLING_INTERVAL', polling);
    },
    getDemoToken(context, password) {
      return new Promise((resolve, reject) => {
        UserApi.getDemoToken(password)
          .then((response) => {
            context.commit('SET_DEMO_TOKEN', response.token);
            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_DEMO_TOKEN', null);
            reject(error.response);
          });
      });
    },
    logout(context) {
      UserApi.logout().then(() => {
        window.localStorage.removeItem('rheto');

        context.commit('SET_USER', null);
        context.commit('SET_AUTHENTICATED', false);
        context.commit('programs/SET_SELECTED_PROGRAM', null, { root: true });
        context.commit('programs/SET_PROGRAMS_STATUS', 'idle', { root: true });
        context.commit('videoAnalysis/SET_SELECTED_VIDEO', null, { root: true });

        router.replace({ name: 'login' });
      });
    },

    checkJwt(context) {
      return new Promise((resolve, reject) => {
        if (import.meta.env.DEV) {
          const token = window.localStorage.getItem('rheto');

          if (!token) {
            const error = new Error('No token found');
            error.authInvalid = true;
            reject(error);
            return;
          }

          // Check token expiration in development
          if (Date.now() >= (JSON.parse(atob(token.split('.')[1]))).exp * 1000) {
            const error = new Error('Token expired');
            error.authInvalid = true;
            reject(error);
            return;
          }
        }

        // For both development and production, let the server validate the token
        UserApi.checkJwt()
          .then((response) => {
            if (response.user) {
              context.commit('SET_USER', response.user);
              context.commit('SET_AUTHENTICATED', true);
              resolve(response);
            } else {
              // 200 but no user — a definitive auth failure. Report it and let
              // the poller's consecutive-failure counter own the logout decision.
              const error = new Error('No user in session');
              error.authInvalid = true;
              reject(error);
            }
          })
          .catch((error) => {
            console.error(error);
            // Do not tear down auth state here. checkJwt only reports; the
            // poller decides to log out after two consecutive definitive
            // failures, so a single blip or fluke 401 can't drop the session.
            reject(error);
          });
      });
    },
    checkEmail(context, email) {
      return new Promise((resolve, reject) => {
        UserApi.checkEmail(email)
          .then((response) => {
            resolve(response);
          }).catch((error) => {
            reject(error);
          });
      });
    },
    saveUserProfile(context, user) {
      return UserApi.saveUserProfile(user)
        .then(() => {
          context.commit('SET_USER', user);
        }).catch((error) => {
          throw error;
        });
    },
    async removeUserProfile(context, user) {
      const prevStatus = user.status;
      try {
        await UserApi.saveUserProfile({ ...user, status: 'Archived' });
        await UserApi.deleteUserProfile(user);
        await context.dispatch('logout');
      } catch (error) {
        try {
          await UserApi.saveUserProfile({ ...user, status: prevStatus });
        } catch (rbErr) {
          console.error('Rollback failed', rbErr);
        }
        throw error;
      }
    },
    setTempUser(context, email) {
      context.commit('SET_TEMP_USER', email);
    },
    getUser(context, userId) {
      return new Promise((resolve, reject) => {
        UserApi.getUser(userId, true)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getUserProgress(context, { userId, programId }) {
      context.commit('SET_LOADING_USER_PROGRESS', true);
      return new Promise((resolve, reject) => {
        UserApi.getUserProgress(userId, programId)
          .then((response) => {
            context.commit('SET_USER_PROGRESS', response);
            context.commit('SET_LOADING_USER_PROGRESS', false);

            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_LOADING_USER_PROGRESS', false);
            reject(error);
          });
      });
    },
    async addCourseProgress(context, { course, completedItems, lastLesson }) {
      try {
        let targetCourse = context.state.userProgress.courses.find(c => c.slug === course.slug);

        const allItems = course.chapters.reduce((acc, chapter) => {
          if (chapter.status === 'Published') {
            const publishedSections = chapter.sections.filter(s => s.status === 'Published');

            return acc + publishedSections.length;
          }

          return acc;
        }, 0);

        if (!targetCourse) {
          targetCourse = {
            course: course._id,
            slug: course.slug,
            completedItems: [],
            progress: 0,
            lastLesson: null,
          };
        }

        targetCourse.completedItems = [
          ...new Set([...targetCourse.completedItems, ...completedItems]),
        ];

        targetCourse.progress = Math.min(
          Math.round((targetCourse.completedItems.length / allItems) * 100),
          100,
        );
        targetCourse.lastLesson = lastLesson;

        context.commit('SET_USER_PROGRESS', {
          ...context.state.userProgress,
          courses: [
            ...context.state.userProgress.courses.filter(c => c.slug !== course.slug),
            targetCourse,
          ],
        });

        const selectedProgram = context.rootState.programs.selectedProgram;

        await UserApi.updateProgress(
          context.state.user._id,
          selectedProgram ? selectedProgram._id : null,
          context.state.userProgress,
        );
      } catch (error) {
        console.error(error);
      }
    },
    async removeCourseProgress(context, { course, incompletedItems, lastLesson }) {
      try {
        let targetCourse = context.state.userProgress.courses.find(c => c.slug === course.slug);

        const allItems = course.chapters.reduce((acc, chapter) => {
          if (chapter.status === 'Published') {
            const publishedSections = chapter.sections.filter(s => s.status === 'Published');

            return acc + publishedSections.length;
          }

          return acc;
        }, 0);

        if (!targetCourse) {
          targetCourse = {
            course: course._id,
            slug: course.slug,
            completedItems: [],
            progress: 0,
            lastLesson: null,
          };
        }

        targetCourse.completedItems = targetCourse.completedItems
          .filter(item => !incompletedItems.includes(item));

        targetCourse.progress = Math.min(
          Math.round((targetCourse.completedItems.length / allItems) * 100),
          100,
        );
        targetCourse.lastLesson = lastLesson;

        if (targetCourse.completedItems.length === 0) {
          context.commit('SET_USER_PROGRESS', {
            ...context.state.userProgress,
            courses: [
              ...context.state.userProgress.courses.filter(c => c.slug !== course.slug),
            ],
          });
        } else {
          context.commit('SET_USER_PROGRESS', {
            ...context.state.userProgress,
            courses: [
              ...context.state.userProgress.courses.filter(c => c.slug !== course.slug),
              targetCourse,
            ],
          });
        }

        const selectedProgram = context.rootState.programs.selectedProgram;

        await UserApi.updateProgress(
          context.state.user._id,
          selectedProgram ? selectedProgram._id : null,
          context.state.userProgress,
        );
      } catch (error) {
        console.error(error);
      }
    },
    async addExerciseProgress(context, exercise) {
      try {
        const exercisesCompleted = context.state.userProgress.exercisesCompleted;
        const isExerciseCompleted = exercisesCompleted.includes(exercise._id);

        if (!isExerciseCompleted) {
          exercisesCompleted.push(exercise._id);

          context.commit('SET_USER_PROGRESS', {
            ...context.state.userProgress,
            exercisesCompleted,
          });

          const selectedProgram = context.rootState.programs.selectedProgram;

          await UserApi.updateProgress(
            context.state.user._id,
            selectedProgram ? selectedProgram._id : null,
            context.state.userProgress,
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    async removeExerciseProgress(context, exercise) {
      try {
        const exercisesCompleted = context.state.userProgress.exercisesCompleted;

        context.commit('SET_USER_PROGRESS', {
          ...context.state.userProgress,
          exercisesCompleted: exercisesCompleted.filter(e => e !== exercise._id),
        });

        const selectedProgram = context.rootState.programs.selectedProgram;

        await UserApi.updateProgress(
          context.state.user._id,
          selectedProgram ? selectedProgram._id : null,
          context.state.userProgress,
        );
      } catch (error) {
        console.error(error);
      }
    },
  },
  mutations: {
    SET_USER: (state, user) => {
      state.user = user;
    },
    SET_AUTHENTICATED: (state, auth) => {
      state.isAuth = auth;
    },
    SET_TEMP_USER: (state, email) => {
      state.tempUser = email;
    },
    SET_DEMO_TOKEN: (state, token) => {
      state.demoToken = token;
    },
    SET_POLLING_INTERVAL: (state, interval) => {
      // If interval exists, clear it
      if (state.interval) {
        clearInterval(state.interval);
      }
      state.interval = interval;
    },
    CLEAR_POLLING_INTERVAL: (state) => {
      clearInterval(state.interval);
      state.interval = null;
    },
    INCREMENT_AUTH_FAILURE: (state) => {
      state.authFailureCount += 1;
    },
    RESET_AUTH_FAILURE: (state) => {
      state.authFailureCount = 0;
    },
    SET_USER_PROGRESS: (state, progress) => {
      state.userProgress.exercisesCompleted = progress.exercisesCompleted || [];
      state.userProgress.sessionsAttended = progress.sessionsAttended || 0;
      state.userProgress.coachEvaluations = progress.coachEvaluations || 0;
      state.userProgress.videosShared = progress.videosShared || 0;
      state.userProgress.videosAnalyzed = progress.videosAnalyzed || 0;
      state.userProgress.courses = progress.courses || [];
      state.userProgress.bigSix = progress.bigSix || {};
      state.userProgress.videoScores = progress.videoScores || {};
      state.userProgress.latestVideo = progress.latestVideo || null;
      state.userProgress.certificate = progress.certificate || null;
    },
    SET_LOADING_USER_PROGRESS: (state, loading) => {
      state.loadingUserProgress = loading;
    },
  },
};
